import { Dialog } from 'antd-mobile'
import CryptoJS from 'crypto-js'

import { isAndroid } from '../wxBridge'

import drawWatermark from './h5origin/drawWatermark'
import { JSSDK, LocationResponse } from './interface'
import { getWxTicket } from './service'
import { callbackToPromise, service } from './utils'
import { WxworkAuthUrl } from './wxwork.config'

const isWeixinSimulator =
  /miniprogramhtmlwebview/i.test(navigator.userAgent) && /wechatdevtools/i.test(navigator.userAgent)
const getLocationOrMock = (params: any) => {
  return isWeixinSimulator
    ? Promise.resolve({
        accuracy: 30,
        cached: 'true',
        errMsg: 'getLocation:ok',
        indoor_building_floor: '1000',
        indoor_building_id: '',
        indoor_building_type: '-1',
        latitude: 31.158525,
        longitude: 121.48121,
        speed: '0.0'
      })
    : callbackToPromise(wx.getLocation, params)
}
declare const wx: any
/**
 * 微信小程序webview api二次封装
 * 参考：https://developer.work.weixin.qq.com/document/path/94315
 */
class Wx implements JSSDK {
  apiAuthStatus: 'pending' | 'done' | 'error' | undefined = 'pending'
  /**
   * 静默登录
   * @param data  { corp_id: corpId, code }
   * @param config ?: { baseURL: string }
   * @returns Promise<IResponseType>
   */
  silenceLogin(data: any, config?: any) {
    return service({
      method: 'post',
      url: '/app/qw/inner/login',
      data,
      config
    })
  }
  /**
   * 手机系统权限
   * 文档：https://open.feishu.cn/document/client-docs/gadget/-web-app-api/open-ability/setting/getsetting
   * @param authType 授权类型 location
   * @returns Promise<boolean>
   */
  checkAuth(authType: string): Promise<boolean> {
    const checkFunc: any = {
      location: getLocationOrMock
    }
    if (checkFunc[authType]) {
      return checkFunc[authType]().then((res: any) => {
        return (res.longitude && res.latitude) || isWeixinSimulator
      })
    } else {
      return Promise.resolve(false)
    }
  }
  /**
   * 暂无
   * @param authType 同checkAuth
   * @returns Promise
   */
  authorize(authType: string): Promise<boolean> {
    return Promise.resolve(true)
  }
  /**
   * 引导打开系统设置
   * @param authType 同checkAuth
   * @returns Promise
   */
  openSystemSetting(param?: any) {
    Dialog.alert({
      title: '无法获取你的位置信息',
      content: isAndroid
        ? '请前往手机系统的设置打开定位服务，并允许微信使用定位服务。'
        : '请在iphone的“设置-隐私-定位服务”选项中打开定位服务，并允许微信使用定位服务。',
      closeOnMaskClick: true,
      confirmText: '关闭'
    })
    return Promise.resolve()
  }
  /**
   * 初始化鉴权接口
   * @param param0 { api: string; corpId?: string }
   * @returns Promise
   */
  config({ api, corpId }: { api?: string; corpId?: string } = {}) {
    // if (this.apiAuthStatus === 'done') {
    //   return Promise.reject(new Error('重复调用'))
    // }
    return new Promise((resolve, reject) => {
      const toResolve = (res: any) => {
        this.apiAuthStatus = 'done'
        resolve(res)
      }
      const toReject = (err: any) => {
        this.apiAuthStatus = 'error'
        reject(err)
      }
      const appId = 'wxe31c343768359dc7'
      // 小程序jsapi_ticket
      // // 获取 jsapi_ticket
      getWxTicket()
        .then((res: any) => {
          const jsapiTicket = res?.data
          // 生成 nonceStr 和 timestamp
          const nonceStr = Math.random()?.toString(36)?.substring(2, 15)
          const timestamp = Math.floor(Date.now() / 1000)
          const redirectUrl = window.location.href

          // 拼接字符串
          const string = `jsapi_ticket=${jsapiTicket}&noncestr=${nonceStr}&timestamp=${timestamp}&url=${redirectUrl}`

          // 生成 SHA-1 签名
          const signature = CryptoJS.SHA1(string).toString(CryptoJS.enc.Hex)
          // 配置微信 JS-SDK
          wx.config({
            debug: false, // 开启调试模式
            appId, // 必填，公众号的唯一标识
            timestamp, // 必填，生成签名的时间戳
            nonceStr, // 必填，生成签名的随机串
            signature, // 必填，签名
            jsApiList: [
              'navigateToMiniProgram',
              'switchTab',
              'chooseImage',
              'openLocation',
              'getLocation',
              'scanQRCode',
              'getLocalImgData',
              'startRecord',
              'stopRecord',
              'onVoiceRecordEnd'
            ] // 必填，需要使用的JS接口列表
          })
          // 监听配置成功事件
          wx.ready(() => {
            console.log('wx.ready')
            toResolve(1)
          })

          // 监听配置失败事件
          wx.error((res: any) => {
            console.log('wx.error', res)
            toReject(res)
          })
        })
        .catch(toReject)
    })
  }
  getAuthCode({
    corpId,
    agentId
  }: {
    corpId: string
    agentId?: string
  }): Promise<{ code: string }> {
    return new Promise((resolve, reject) => {
      const url = `${WxworkAuthUrl}?appid=${corpId?.trim?.()}&redirect_uri=${encodeURIComponent(location.href.trim())}&response_type=code&scope=snsapi_privateinfo&state=&agentid=${agentId?.trim?.()}#wechat_redirect`
      location.href = url
      reject(new Error('跳转到授权页'))
    })
  }
  getLocation(): Promise<LocationResponse> {
    console.log('我调用了getLocation:', getLocationOrMock)
    return getLocationOrMock({ type: 'gcj02' }).then((res: any) => {
      return {
        ...res,
        accuracy: Number(res.accuracy || 0)
      }
    })
  }
  getHighLocation() {
    return getLocationOrMock({ type: 'gcj02' })
  }
  startLocation(): Promise<LocationResponse> {
    return getLocationOrMock({ type: 'gcj02' })
  }
  stopLocation() {
    return new Promise((resolve, reject) => {
      resolve(true)
    })
  }
  scanCode() {
    return callbackToPromise(wx.scanQRCode, { needResult: 1, scanType: ['qrCode'] }).then(
      (res: any) => res?.resultStr
    )
  }
  takePhoto(param?: any): Promise<string> {
    return new Promise((resolve, reject) => {
      callbackToPromise(wx.chooseImage, {
        count: 1,
        sourceType: ['camera'],
        sizeType: ['compressed'],
        isSaveToAlbum: false,
        ...param
      })
        .then((res: any) => {
          const path = res.localIds?.[0]
          callbackToPromise(wx.getLocalImgData, { localId: path, compressionRatio: 0.3 })
            .then((res: any) => res.localData)
            .then((base64Data: any) => {
              console.error('文件转换', base64Data)
              if (base64Data) {
                drawWatermark(
                  base64Data?.startsWith('data:image/')
                    ? base64Data
                    : `data:image/jpeg;base64,${base64Data}`,
                  param?.stickers
                )
                  .then(resolve)
                  .catch(reject)
              } else {
                reject(new Error('拍照失败'))
              }
            })
            .catch(reject)
        })
        .catch(reject)
    })
  }
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  setTitle(params?: any): Promise<void | {}> {
    // 无需实现，自动使用document.title
    return Promise.resolve()
  }
  getImageInfo(localId?: string): Promise<string> {
    // data:image/jpeg;base64,
    return new Promise((resolve, reject) => {
      callbackToPromise(wx.getLocalImgData, { localId, compressionRatio: 0.3 })
        .then((res: any) => resolve(res.localData))
        .catch(reject)
    })
  }
  vibrate(duration = 15) {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      // H5环境且浏览器支持震动
      navigator.vibrate(duration)
      return Promise.resolve(true)
    }
    return Promise.resolve(false)
  }
  openDocument(filePath: string, fileType: string) {
    wx.miniProgram.navigateTo({
      url: `/pages/Document/index?fileType=${fileType}&filePath=${encodeURIComponent(filePath)}`
    })
  }
}
export default new Wx()
